<template>
  <div id="gatewaySnmpLogs">
    <card :title="$t('g-snmp:filter')" :visible="false" collapsible>
      <div slot="card-body">
        <div class="form-row">
          <div class="form-group col-md-4">
            <label for="period" class="control-label">{{ $t('g-snmp:period') | capitalize }}</label>
            <flat-pickr id="period"
                        v-model="filterPeriod"
                        :config="fpConfig"
                        name="period"
                        class="form-control"
            />
            <div class="invalid-feedback">{{ errors.first('period') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="filterRequestId" class="control-label">{{ $t('g-snmp:requestId') | capitalize }}</label>
            <input id="filterRequestId"
                   v-model="filterRequestId"
                   v-validate="'text'"
                   :data-vv-as="$t('g-snmp:requestId')"
                   :class="{ 'is-invalid': errors.has('filterRequestId') }"
                   name="filterRequestId"
                   class="form-control"
                   type="text"
            >
            <div class="invalid-feedback">{{ errors.first('filterRequestId') }}</div>
          </div>

          <div class="form-group col-md-4">
            <label for="filterAction">{{ $t('g-snmp:action') | capitalize }}</label>
            <div class="input-group">
              <multiselect id="filterAction"
                           v-model="filterAction"
                           :options="actions"
                           :show-labels="false"
                           :options-limit="200"
                           :placeholder="$t('g-snmp:searchAction') | capitalize"
              >
                <span slot="noResult">{{ $t('g-snmp:noActions') | capitalize }}</span>
              </multiselect>
              <span class="input-group-append">
                <button id="resetActionButton" class="btn btn-flat btn-sm" type="button" @click="filterAction=''"><i class="icon-remove" /></button>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div slot="card-footer">
        <button id="filterButton" class="btn btn-primary" @click="filter()">{{ $t('g-snmp:search') | capitalize }}</button>
      </div>
    </card>

    <grid id="gatewaySnmpLogsGrid"
          :title="$t('g-snmp:snmpLogs')"
          :columns="columns"
          :paginated-dto="snmpRows"
          :fixed="true"
          :custom-export-label="$t('g-snmp:exportLabel')"
          @load="filter"
    >
      <colgroup slot="colgroup"><col span="5"><col style="width: 55%;"></colgroup>

      <template slot="header-content">
        {{ $t('g-snmp:content') | capitalize }}
        <span class="float-right">
          <input id="oid" v-model="oid" type="checkbox">
          <label for="oid">{{ $t('g-snmp:displayOidNames') | capitalize }}</label>
        </span>
      </template>

      <template slot="date" slot-scope="props">{{ props.dto.createTimeStamp | date }}</template>
      <template slot="content" slot-scope="props">
        <span v-if="!oid">{{ props.dto.content }}</span>
        <span v-else>{{ parseOid(props.dto.content) }}</span>
      </template>
    </grid>
  </div>
</template>

<script>
import { isEmpty } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import snmpActions from '@/configuration/snmpActions'

export default {
  titleKey: 'gatewaySnmp',

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      // Filters
      filterPeriod: '',
      filterRequestId: '',
      filterAction: '',
      // Data
      snmpRows: {},
      // Display oid name/code
      oid: false,

      // In data to be modified if needed
      fpConfig: {
        mode: 'range',
        enableTime: true,
        altInput: true,
        altFormat: 'Y/m/d H:i',
        dateFormat: 'U',
        time_24hr: true
      }
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    actions () {
      return snmpActions.sort()
    },

    columns () {
      return [
        { text: this.$t('g-snmp:id'), id: 'id' },
        { text: this.$t('g-snmp:date'), slot: 'date' },
        { text: this.$t('g-snmp:requestId'), id: 'requestId' },
        { text: this.$t('g-snmp:action'), id: 'action' },
        { text: this.$t('g-snmp:origin'), formatter: snmpLog => snmpLog.origin.replace(/_/g, ' ') },
        { headerSlot: 'header-content', slot: 'content' }
      ]
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.snmpRows = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.snmpRows)) return

      this.filter()
    }
  },

  created () {
    this.filter()
  },

  methods: {
    parseOid (str) { return this.$utils.parseOid(str) },

    async filter (options = {}) {
      const valid = await this.$validator.validateAll()
      if (!valid) return

      if (!isEmpty(this.gateway)) {
        // Add criteria
      // Flatpickr return a string "<from> to <to>" with <from> and <to> dates in seconds
        const filterPeriod = this.filterPeriod ? this.filterPeriod.split(' to ').map(v => v * 1000) : []
        if (!isEmpty(filterPeriod)) {
          options.startDate = filterPeriod[0]
          options.endDate = filterPeriod[1]
        }

        if (this.filterRequestId) options.requestId = this.filterRequestId
        if (this.filterAction) options.action = this.filterAction

        // Get filtered data
        this.getSnmpLogs(options)
      }
    },

    async getSnmpLogs (options) {
      const params = this.$utils.getParams({ sort: '-date', startDate: '', endDate: '', requestId: '', action: '' }, options, this.queryPageParams)
      let error, response
      [error, response] = await to(this.$api.getGatewaySnmpLogs(this.gateway, params))
      if (error || !response) return

      this.snmpRows = response.data
    }
  }
}
</script>
